import { Container, Drawer, Grid, Typography, Button } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import CheckboxDropdown, { CheckboxDropdownItem } from '../TopFilterBar/checkbox_dropdown';
import styles from './style.module.scss';
import { getFilterLabel, TopFilters } from '../TopFilterBar';
import { useEffect, useState } from 'react';

type FilterDrawerProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  filters: TopFilters;
  options: TopFilters;
  onApplyFilters: (filters: TopFilters) => void;
};

const FilterDrawer = ({ open, setOpen, filters, options, onApplyFilters }: FilterDrawerProps) => {
  const [filterCopy, setFilterCopy] = useState<TopFilters>(filters);
  
  const states = filterCopy.states;
  const cities = filterCopy.cities;
  const propertyTypes = filterCopy.propertyTypes;
  const saleLease = filterCopy.saleLease;

  useEffect(() => {
    if (open) {
      setFilterCopy(filters);
    }
  }, [filters, open]);

  const handleTogglePropertyType = (value: string) => setFilterCopy({ ...filterCopy, propertyTypes: filterCopy.propertyTypes.includes(value) ? filterCopy.propertyTypes.filter(v => v !== value) : [...filterCopy.propertyTypes, value]});
  const handleToggleState = (value: string) => setFilterCopy({ ...filterCopy, states: filterCopy.states.includes(value) ? filterCopy.states.filter(v => v !== value) : [...filterCopy.states, value], cities: filterCopy.cities });
  const handleToggleCity = (value: string) => setFilterCopy({ ...filterCopy, cities: filterCopy.cities.includes(value) ? filterCopy.cities.filter(v => v !== value) : [...filterCopy.cities, value]});
  const handleToggleSaleLease = (value: string) => setFilterCopy({ ...filterCopy, saleLease: filterCopy.saleLease.includes(value) ? filterCopy.saleLease.filter(v => v !== value) : [...filterCopy.saleLease, value]});
  
  const handleToggleAllStates = (checked: boolean) => setFilterCopy({ ...filterCopy, states: checked ? [...options.states] : [], cities: filterCopy.cities });
  const handleToggleAllCities = (checked: boolean) => setFilterCopy({ ...filterCopy, cities: checked ? [...options.cities] : []});
  const handleToggleAllPropertyTypes = (checked: boolean) => setFilterCopy({ ...filterCopy, propertyTypes: checked ? [...options.propertyTypes] : []});
  const handleToggleAllSaleLease = (checked: boolean) => setFilterCopy({ ...filterCopy, saleLease: checked ? [...options.saleLease] : []});
  

  const propertyTypeItems: CheckboxDropdownItem[] = options.propertyTypes.map(opt => ({ key: opt, label: opt, checked: filterCopy.propertyTypes.includes(opt) }));
  const stateItems: CheckboxDropdownItem[] = options.states.map(opt => ({ key: opt, label: opt, checked: filterCopy.states.includes(opt) }));
  const cityItems: CheckboxDropdownItem[] = options.cities.map(opt => ({ key: opt, label: opt, checked: filterCopy.cities.includes(opt) }));
  const saleLeaseItems: CheckboxDropdownItem[] = options.saleLease.map(opt => ({ key: opt, label: opt, checked: filterCopy.saleLease.includes(opt) }));

  const propertyTypeLabel = getFilterLabel(options.propertyTypes.length, propertyTypes.length, selected => `${selected} Property Types Selected`, 'All Property Types');
  const stateLabel = getFilterLabel(options.states.length, states.length, selected => `${selected} States Selected`, 'All States');
  const cityLabel = getFilterLabel(options.cities.length, cities.length, selected => `${selected} Cities Selected`, 'All Cities');
  const saleLeaseLabel = saleLease.length === options.saleLease.length 
    ? saleLease.join(', ') 
    : saleLease.length === 0 
      ? 'Select Sale/Lease' 
      : saleLease.join(', ');

  const handleApplyFilters = () => {
    onApplyFilters(filterCopy);
    setOpen(false);
  };

  return (
    <Drawer
      open={open}
      onClose={() => { setOpen(false); }}
      anchor="right"
      PaperProps={{ className: styles.drawerPaper }}
    >
      <Container maxWidth="lg" className={styles['drawer-content']}>
        <Grid container direction="column" sx={{ height: 'inherit' }}>
          <Grid item className={styles.drawerHeader}>
            <Typography variant="h6" className={styles.filtersTitle}>
              FILTERS
            </Typography>
            <IconButton
              aria-label="close"
              onClick={() => setOpen(false)}
              className={styles.closeButton}
            >
              <CloseIcon fontSize='medium' />
            </IconButton>
          </Grid>

          <Grid item xs className={styles.filterSections}>
            <div className={styles.filterSection}>
              <Typography variant="subtitle1" className={styles.filterLabel}>
                Sale or Lease
              </Typography>
              <CheckboxDropdown
                fullWidth
                id="mobile-sale-lease"
                buttonLabel={saleLeaseLabel}
                items={saleLeaseItems}
                onToggle={handleToggleSaleLease}
                onToggleAll={handleToggleAllSaleLease}
                menuAriaLabel="Select sale or lease"
              />
            </div>

            <div className={styles.filterSection}>
              <Typography variant="subtitle1" className={styles.filterLabel}>
                Property Types
              </Typography>
              <CheckboxDropdown
                fullWidth
                id="mobile-property-types"
                buttonLabel={propertyTypeLabel}
                items={propertyTypeItems}
                onToggle={handleTogglePropertyType}
                onToggleAll={handleToggleAllPropertyTypes}
                menuAriaLabel="Select property types"
              />
            </div>

            <div className={styles.filterSection}>
              <Typography variant="subtitle1" className={styles.filterLabel}>
                States
              </Typography>
              <CheckboxDropdown
                fullWidth
                id="mobile-states"
                buttonLabel={stateLabel}
                items={stateItems}
                onToggle={handleToggleState}
                onToggleAll={handleToggleAllStates}
                menuAriaLabel="Select states"
              />
            </div>

            <div className={styles.filterSection}>
              <Typography variant="subtitle1" className={styles.filterLabel}>
                Cities
              </Typography>
              <CheckboxDropdown
                fullWidth
                id="mobile-cities"
                buttonLabel={cityLabel}
                items={cityItems}
                onToggle={handleToggleCity}
                onToggleAll={handleToggleAllCities}
                menuAriaLabel="Select cities"
              />
            </div>
          </Grid>

          <Grid item container justifyContent="center">
            <Button
              variant="contained"
              fullWidth
              onClick={handleApplyFilters}
              className={styles.applyButton}
              size="large"
            >
              Apply Filters
            </Button>
          </Grid>
        </Grid>
      </Container>
    </Drawer>
  );
};

export default FilterDrawer;